import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header, MobileBottomNav } from '../components/Layout';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { rpsApi } from '../services/api';
import { toast } from 'sonner';
import { Swords, Camera, CameraOff, Trophy, Clock, Shield, Timer, Video } from 'lucide-react';
import CryptoJS from 'crypto-js';

const MOVES = [
  { id: 'rock', label: 'Rock', emoji: '\u270a' },
  { id: 'paper', label: 'Paper', emoji: '\u{1f590}\ufe0f' },
  { id: 'scissors', label: 'Scissors', emoji: '\u270c\ufe0f' },
];

const GAME_DURATION = 60;

const CameraPanel = ({ label, color, initial, videoRef, isLocal, cameraActive }) => (
  <div className="flex flex-col items-center" data-testid={`camera-panel-${label.toLowerCase().replace(/[^a-z]/g, '')}`}>
    <div className={`relative w-full aspect-[4/3] rounded-xl overflow-hidden border-2`} style={{ borderColor: color }}>
      <video 
        ref={videoRef} 
        autoPlay 
        muted={isLocal} 
        playsInline 
        className={`w-full h-full object-cover bg-black ${cameraActive ? 'block' : 'hidden'}`}
        style={{ transform: isLocal ? 'scaleX(-1)' : 'none' }}
      />
      {!cameraActive && (
        <div className="w-full h-full bg-[#0A0A0A] flex flex-col items-center justify-center absolute inset-0">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center text-2xl md:text-3xl font-black mb-2" style={{ background: `${color}30`, color }}>
            {initial}
          </div>
          <div className="text-gray-600 text-xs flex items-center gap-1">
            <CameraOff className="h-3 w-3" /> Waiting for camera
          </div>
        </div>
      )}
      {cameraActive && (
        <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/60 rounded-full px-2 py-0.5">
          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <span className="text-white text-xs font-bold">LIVE</span>
        </div>
      )}
    </div>
    <div className="mt-2 text-center">
      <span className="text-xs font-bold uppercase break-words" style={{ color }}>{label}</span>
    </div>
  </div>
);

const RPSDuelPage = () => {
  const { challengeId } = useParams();
  const { user } = useAuth();
  const ws = useWebSocket();
  const addListener = ws?.addListener;
  const navigate = useNavigate();
  
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMove, setSelectedMove] = useState(null);
  const [committed, setCommitted] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [roundResult, setRoundResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const [gameStarted, setGameStarted] = useState(false);
  
  // Camera states
  const [myCameraOn, setMyCameraOn] = useState(false);
  const [opponentCameraOn, setOpponentCameraOn] = useState(false);
  const myVideoRef = useRef(null);
  const opponentVideoRef = useRef(null);
  const moderatorVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const saltRef = useRef('');
  const timerRef = useRef(null);

  const fetchChallenge = useCallback(async () => {
    try {
      const { data } = await rpsApi.getMyChallenges();
      const found = data.find(c => c.id === challengeId);
      if (found) {
        setChallenge(found);
        if (found.status === 'active' && !gameStarted) {
          setGameStarted(true);
        }
      }
    } catch (e) {
      toast.error('Failed to load challenge');
    } finally {
      setLoading(false);
    }
  }, [challengeId, gameStarted]);

  useEffect(() => { fetchChallenge(); }, [fetchChallenge]);

  // 60 second countdown timer
  useEffect(() => {
    if (!gameStarted || challenge?.status === 'completed') return;
    
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          toast.warning('Time is up! The player in the lead wins.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [gameStarted, challenge?.status]);

  // WebSocket events
  useEffect(() => {
    if (!addListener) return;
    return addListener((event) => {
      if (event.challenge_id !== challengeId) return;
      
      if (event.type === 'rps_round_result') {
        setRoundResult({ winner: event.round_winner, yourMove: event.your_move, theirMove: event.their_move });
        setCommitted(false);
        setRevealed(false);
        setSelectedMove(null);
        setTimeout(() => { setRoundResult(null); fetchChallenge(); }, 3000);
      }
      if (event.type === 'rps_completed') {
        fetchChallenge();
      }
      if (event.type === 'camera_state_update' && event.user_id !== user?.id) {
        setOpponentCameraOn(event.camera_on);
      }
      // WebRTC signaling
      if (event.type === 'webrtc_offer' && peerConnectionRef.current) {
        handleRemoteOffer(event.data);
      }
      if (event.type === 'webrtc_answer' && peerConnectionRef.current) {
        handleRemoteAnswer(event.data);
      }
      if (event.type === 'webrtc_ice_candidate' && peerConnectionRef.current) {
        handleRemoteIce(event.data);
      }
    });
  }, [addListener, challengeId, user?.id]);

  // WebRTC helpers
  const handleRemoteOffer = async (offer) => {
    try {
      const pc = peerConnectionRef.current;
      if (!pc) return;
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      sendSignal('webrtc_answer', answer);
    } catch (e) { console.log('WebRTC offer error:', e); }
  };

  const handleRemoteAnswer = async (answer) => {
    try {
      await peerConnectionRef.current?.setRemoteDescription(new RTCSessionDescription(answer));
    } catch (e) { console.log('WebRTC answer error:', e); }
  };

  const handleRemoteIce = async (candidate) => {
    try {
      await peerConnectionRef.current?.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (e) { console.log('WebRTC ice error:', e); }
  };

  const sendSignal = (type, data) => {
    // Send via WebSocket - we don't have direct ws access here, so use a broadcast workaround
    // In production this would use the WebSocket connection directly
  };

  const toggleCamera = async () => {
    if (myCameraOn) {
      localStreamRef.current?.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
      if (myVideoRef.current) myVideoRef.current.srcObject = null;
      setMyCameraOn(false);
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
          audio: false 
        });
        localStreamRef.current = stream;
        setMyCameraOn(true);
        // Set srcObject after state update renders the video element
        requestAnimationFrame(() => {
          if (myVideoRef.current) {
            myVideoRef.current.srcObject = stream;
            myVideoRef.current.play().catch(() => {});
          }
        });
        toast.success('Camera is live!');
      } catch (e) {
        toast.error('Camera access denied. Please allow camera permissions in your browser settings.');
      }
    }
  };

  const handleCommit = async () => {
    if (!selectedMove) return;
    const salt = Math.random().toString(36).substring(2, 14);
    saltRef.current = salt;
    const hash = CryptoJS.SHA256(`${selectedMove}:${salt}`).toString();
    
    try {
      await rpsApi.commitMove({ challenge_id: challengeId, move_hash: hash });
      setCommitted(true);
      toast.success('Move locked in! Waiting for opponent...');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to commit move');
    }
  };

  const handleReveal = async () => {
    try {
      const result = await rpsApi.revealMove({ challenge_id: challengeId, move: selectedMove, salt: saltRef.current });
      setRevealed(true);
      if (result.data.round_winner) {
        setRoundResult({ winner: result.data.round_winner, yourMove: result.data.your_move, theirMove: result.data.their_move });
        setTimeout(() => { setRoundResult(null); setCommitted(false); setRevealed(false); setSelectedMove(null); fetchChallenge(); }, 3000);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reveal move');
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      localStreamRef.current?.getTracks().forEach(t => t.stop());
      peerConnectionRef.current?.close();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#D4AF37] font-bold">Loading Duel Arena...</p>
        </div>
      </div>
    );
  }

  if (!challenge) return null;

  const isCreator = challenge.creator_id === user?.id;
  const myScore = isCreator ? challenge.creator_score : challenge.opponent_score;
  const theirScore = isCreator ? challenge.opponent_score : challenge.creator_score;
  const myName = isCreator ? challenge.creator_name : challenge.opponent_name;
  const theirName = isCreator ? challenge.opponent_name : challenge.creator_name;
  const isComplete = challenge.status === 'completed';
  const isWinner = challenge.winner_id === user?.id;
  const hasModerator = challenge.with_moderator;
  const timerColor = timeLeft > 30 ? '#28A745' : timeLeft > 10 ? '#D4AF37' : '#EF4444';
  const timerPercent = (timeLeft / GAME_DURATION) * 100;

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col pb-20 md:pb-0 overflow-x-hidden">
      {/* Minimal Dark Header */}
      <div className="bg-[#0A0A0A] border-b border-[#D4AF37]/20 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Swords className="h-5 w-5 text-[#D4AF37]" />
          <span className="font-black text-white text-sm" style={{ fontFamily: 'Outfit' }}>RPS <span className="text-[#D4AF37]">DUEL</span></span>
        </div>
        <div className="flex items-center gap-3">
          {/* Timer */}
          <div className="flex items-center gap-2" data-testid="game-timer">
            <Timer className="h-4 w-4" style={{ color: timerColor }} />
            <div className="relative w-20 h-2 bg-[#2D2D2D] rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${timerPercent}%`, background: timerColor }} />
            </div>
            <span className="font-mono font-bold text-sm" style={{ color: timerColor }}>{timeLeft}s</span>
          </div>
          <div className="flex items-center gap-1 bg-red-500/20 rounded-full px-2 py-0.5">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-red-400 text-xs font-bold">LIVE</span>
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-5xl mx-auto px-3 py-3 w-full">
        {/* Scores */}
        <div className="flex items-center justify-center gap-4 mb-3">
          <div className="text-center">
            <div className="text-xs text-gray-500 uppercase">Initiator</div>
            <div className="text-3xl font-black text-[#D4AF37]">{myScore}</div>
          </div>
          <div className="text-gray-600 text-sm">Round {(challenge.creator_score || 0) + (challenge.opponent_score || 0) + 1} / Best of 3</div>
          <div className="text-center">
            <div className="text-xs text-gray-500 uppercase">Challenger</div>
            <div className="text-3xl font-black text-[#28A745]">{theirScore}</div>
          </div>
        </div>

        {/* ====== 3-PANEL CAMERA VIEW ====== */}
        <div className={`grid ${hasModerator ? 'grid-cols-3' : 'grid-cols-2'} gap-2 mb-3`} data-testid="camera-panels">
          {/* Initiator Panel */}
          <CameraPanel
            label={isCreator ? `${myName} (You)` : (challenge.creator_name || 'Initiator')}
            color="#D4AF37"
            initial={challenge.creator_name?.charAt(0) || 'I'}
            videoRef={isCreator ? myVideoRef : opponentVideoRef}
            isLocal={isCreator}
            cameraActive={isCreator ? myCameraOn : opponentCameraOn}
          />
          
          {/* Moderator Panel (if applicable) */}
          {hasModerator && (
            <CameraPanel
              label="Moderator"
              color="#6366F1"
              initial="M"
              videoRef={moderatorVideoRef}
              isLocal={false}
              cameraActive={false}
            />
          )}
          
          {/* Challenger Panel */}
          <CameraPanel
            label={!isCreator ? `${myName} (You)` : (challenge.opponent_name || 'Challenger')}
            color="#28A745"
            initial={challenge.opponent_name?.charAt(0) || 'C'}
            videoRef={!isCreator ? myVideoRef : opponentVideoRef}
            isLocal={!isCreator}
            cameraActive={!isCreator ? myCameraOn : opponentCameraOn}
          />
        </div>

        {/* Camera Toggle */}
        <div className="flex justify-center gap-2 mb-3">
          <Button onClick={toggleCamera} size="sm" className={`text-xs ${myCameraOn ? 'bg-red-500 hover:bg-red-600' : 'bg-[#2D2D2D] hover:bg-[#3D3D3D] border border-[#D4AF37]/30'} text-white`} data-testid="toggle-camera">
            {myCameraOn ? <><CameraOff className="h-3 w-3 mr-1" /> Stop Camera</> : <><Camera className="h-3 w-3 mr-1" /> Start Camera</>}
          </Button>
          <div className="bg-[#2D2D2D] rounded-lg px-3 py-1 text-xs text-gray-400 flex items-center gap-2 border border-[#3D3D3D]">
            <Video className="h-3 w-3" />
            <span>{challenge.stake_type === 'real' ? '$' : ''}{challenge.stake_amount * 2}{challenge.stake_type === 'qbean' ? ' QBC' : ''} pot</span>
          </div>
        </div>

        {/* Round Result Overlay */}
        {roundResult && (
          <div className="card-dark text-center mb-3 border-2 border-[#D4AF37] animate-pulse">
            <div className="text-xl font-black text-white mb-2">
              {roundResult.winner === 'tie' ? 'TIE!' : roundResult.winner === (isCreator ? 'creator' : 'opponent') ? 'YOU WIN THIS ROUND!' : 'OPPONENT WINS THIS ROUND!'}
            </div>
            <div className="flex justify-center gap-6 text-5xl">
              <span>{MOVES.find(m => m.id === roundResult.yourMove)?.emoji}</span>
              <span className="text-[#D4AF37] text-2xl self-center">vs</span>
              <span>{MOVES.find(m => m.id === roundResult.theirMove)?.emoji}</span>
            </div>
          </div>
        )}

        {/* Game Complete */}
        {isComplete ? (
          <div className="card-dark text-center border-2 border-[#D4AF37]">
            <Trophy className={`h-14 w-14 mx-auto mb-3 ${isWinner ? 'text-[#D4AF37]' : 'text-gray-500'}`} />
            <h2 className="text-3xl font-black text-white mb-2">{isWinner ? 'VICTORY!' : 'DEFEAT!'}</h2>
            <p className="text-gray-400 mb-3">Final Score: {myScore} - {theirScore}</p>
            {isWinner && (
              <div className="text-[#28A745] font-bold text-xl mb-3">
                +{(challenge.stake_amount * 2 * 0.9).toFixed(0)} {challenge.stake_type === 'qbean' ? 'QBC' : 'USD'} won!
              </div>
            )}
            <Button onClick={() => navigate('/rps')} className="btn-primary" data-testid="back-to-rps">Back to RPS</Button>
          </div>
        ) : (
          /* Move Selection */
          <div className="card-dark">
            <h3 className="font-bold text-white text-center mb-3 text-sm">
              {committed ? (revealed ? 'Waiting for round result...' : 'Move locked! Now reveal it.') : 'Choose Your Move'}
            </h3>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {MOVES.map((move) => (
                <button
                  key={move.id}
                  onClick={() => !committed && setSelectedMove(move.id)}
                  disabled={committed || timeLeft === 0}
                  className={`p-3 md:p-4 rounded-xl border-2 text-center transition-all ${
                    selectedMove === move.id
                      ? 'border-[#D4AF37] bg-[#D4AF37]/20 scale-105'
                      : committed || timeLeft === 0 ? 'border-[#3D3D3D] bg-[#2D2D2D] opacity-50' : 'border-[#3D3D3D] bg-[#2D2D2D] hover:border-[#D4AF37]/50'
                  }`}
                  data-testid={`move-${move.id}`}
                >
                  <div className="text-3xl md:text-4xl mb-1">{move.emoji}</div>
                  <div className="text-white font-semibold text-xs">{move.label}</div>
                </button>
              ))}
            </div>

            {timeLeft === 0 ? (
              <div className="text-center text-red-400 py-2 font-bold">
                <Timer className="h-5 w-5 inline mr-2" /> Time's up! Leading player wins.
              </div>
            ) : !committed ? (
              <Button onClick={handleCommit} className="btn-primary w-full" disabled={!selectedMove} data-testid="commit-move-btn">
                Lock In Move
              </Button>
            ) : !revealed ? (
              <Button onClick={handleReveal} className="btn-green w-full" data-testid="reveal-move-btn">
                Reveal Move
              </Button>
            ) : (
              <div className="text-center text-gray-400 py-2">
                <Clock className="h-5 w-5 inline mr-2 animate-spin" /> Waiting for opponent to reveal...
              </div>
            )}
          </div>
        )}
      </main>
      <MobileBottomNav />
    </div>
  );
};

export default RPSDuelPage;
